/**
 * The cookie banner's clearance rule (ui#758, reshaped by ui#1587) — plain TypeScript, no React.
 *
 * `consent/CookieBanner` floats its Bar over the bottom of the viewport, so a control that comes to
 * rest inside that strip is unreachable until the visitor answers the banner. ui#556 is the worst
 * case: a page that fits has no scroll room at all, so no gesture can free the control — which is why
 * `scroll-margin-bottom` alone cannot solve it. A scroll-into-view only steers a scroll that has
 * somewhere to go.
 *
 * ui#758 answered that by reserving the bar's whole strip on EVERY page. That reservation is what
 * ui#1587 removes: this rule mints only the room a control actually lacks, so a page whose own
 * content already carries the control clear of the bar pays nothing, and no page pays more than the
 * strip is worth. The room itself is scrollable overflow rather than layout height — see the
 * `::after` spacer on `Content` in `src/AppComponents.tsx`.
 */

/** Breathing room demanded ABOVE the Bar before a control counts as clear of it. */
export const CONSENT_BAR_GAP_PX = 16;

/**
 * The height of the strip the Bar makes unusable, gap included. This is also the CEILING on any
 * clearance: nothing is ever reserved beyond what the Bar itself covers, which keeps the rule's
 * output bounded by the constant ui#758 used to apply unconditionally.
 *
 * `barInset` is the MEASURED distance from the viewport's bottom edge to the Bar's own bottom edge,
 * never a constant (ui#2704): the Bar floats 16px up on a wide window and docks to the edge on a
 * phone, where the first-layer notice grew by a sentence and the recipient card had no 16px to
 * spare for a strip the Bar does not cover.
 */
export function consentBarReserve(barHeight: number, barInset: number): number {
	return barHeight <= 0 ? 0 : barHeight + barInset + CONSENT_BAR_GAP_PX;
}

/** One control the visitor could scroll towards, measured against the CURRENT scroll positions. */
export interface ScrollReachableControl {
	/** Its bottom edge in viewport pixels. */
	bottom: number;
	/** How far every scroll container between it and the app scroller can still travel downwards. */
	liftable: number;
}

/**
 * The lowest point any of these controls can still be brought to — where the bottom-most one comes
 * to rest once the visitor has scrolled everything as far as it goes.
 *
 * `bottom - liftable` is invariant under scrolling (a gesture lowers both by the same amount), so
 * this answers "how low is the page's last control, at rest" without depending on where the visitor
 * happens to be right now. An empty page has no control to trap, hence 0.
 */
export function lowestRestingBottom(controls: Iterable<ScrollReachableControl>): number {
	let lowest = 0;
	let seen = false;
	for (const {bottom, liftable} of controls) {
		const resting = bottom - liftable;
		if (!seen || resting > lowest) {
			lowest = resting;
			seen = true;
		}
	}
	return lowest;
}

export interface ConsentClearanceInput {
	/** The Bar's measured height; 0 before it has rendered. */
	barHeight: number;
	/** The Bar's measured distance from the viewport's bottom edge; see {@link consentBarReserve}. */
	barInset: number;
	/** `window.innerHeight`. */
	viewportHeight: number;
	/** {@link lowestRestingBottom} over every control the app scroller can reach. */
	restingBottom: number;
	/**
	 * A surface declaring `[data-viewport-locked]` (the recipient page, ui#687) is never given room: it
	 * is pinned to the viewport and reserves the Bar's strip in its own layout, scrolling its content
	 * inside itself when that content is taller than the window (ui#2457).
	 */
	viewportLocked: boolean;
}

/**
 * Scrollable room to mint below the content, in CSS pixels.
 *
 * Zero whenever the page already carries its last control clear of the Bar, which is the common case
 * — that is the reservation ui#1587 set out to stop paying for.
 */
export function consentClearance({
	barHeight,
	barInset,
	viewportHeight,
	restingBottom,
	viewportLocked,
}: ConsentClearanceInput): number {
	const reserve = consentBarReserve(barHeight, barInset);
	if (reserve === 0 || viewportLocked) {
		return 0;
	}
	// Rounded UP: a control resting half a pixel inside the strip is still inside it.
	const deficit = Math.ceil(restingBottom - (viewportHeight - reserve));
	return Math.max(0, Math.min(reserve, deficit));
}
